import React, { useEffect, useState } from 'react';
import styles from './OttServiceOptionsSmallPopup.module.scss';

import RichText from '../../richtext';
import {
  AssetPathPropConfig,
  LineTableBasicPropConfig
} from '../../../services/LineTableBasicPropConfig';
import { CMSStoryblokServiceV2 } from '@sunrise-upc/ajax-lib';
import { countZero, viewport } from '../../../utils/utils';
import { SBENDPOINT } from '../../../services/api-constants';
import OttServiceOptionsLoader from './OttServiceOptionsLoader/OttServiceOptionsLoader';
import DynamicPriceSection from '../DynamicPriceSection';
import FalconButtonV2 from '../../FalconButtonV2/FalconButtonV2';
import { trackNewDatalayerEvent } from '../../../utils/AnalyticsUtils';
import {
  ALL,
  CMS,
  CTABUTTONCLICK,
  GAEVENT,
  POPUPPROMO,
  SELECTPROMOTION,
  VIEWOPTIONPOPUP,
  VIEWPROMOTION
} from '../../../Analytics/AnalyticsConstants';
import CarouselComponent from '../../sharedCarousel/CarouselComponent';

interface OttServiceOptionsSmallPopupProps {
  content: any;
  addOptionsCallBack: (option?: any[] | undefined) => void;
  metadata: any;
  constants: any;
  mainContent: any;
  preSelectedOptions: any[];
  optionsResponse: any[];
  updateResponseList?: (list: any) => void;
  showPrice?: any;
  productData?: any;
  offeringData?: any;
  uniqueId?: any;
  deviceFlow?: any;
}

const OttServiceOptionsSmallPopup = ({
  content,
  addOptionsCallBack,
  metadata,
  constants,
  mainContent,
  preSelectedOptions,
  optionsResponse,
  updateResponseList,
  showPrice,
  productData,
  offeringData,
  uniqueId,
  deviceFlow
}: OttServiceOptionsSmallPopupProps) => {
  const sbEndpoint = LineTableBasicPropConfig?.getInstance()?.getSBUrl();
  const assetPath = AssetPathPropConfig?.getInstance()?.getAssetPath();

  const activelocaleId: string | undefined = metadata?.lang;
  const [sbData, setSbData] = useState([]);
  const [optionsList, setOptionsList] = useState<any>();
  const [selectedOption, setSelectedOption] = useState<any>(preSelectedOptions?.[0]);
  const viewPort = viewport();

  const selectionMode = content?.selectionMode;

  //on click of options
  const clickHandler = (option?: any, index?: number) => {
    if (selectionMode) {
      setSelectedOption(option);
      optionCallBack(option ? option : undefined);
    } else {
      optionCallBack(option ? option : undefined);
    }
  };

  const optionCallBack = (option?: any, index?: number) => {
    trackNewDatalayerEvent(SELECTPROMOTION, SELECTPROMOTION, {
      banner_promotion_name: option?.title || 'skip_to_checkout',
      banner_source: CMS,
      banner_type: POPUPPROMO,
      banner_location: POPUPPROMO,
      audience_name: ALL,
      promotion_objective: ALL,
      index: index ? index + 1 : 1
    });

    addOptionsCallBack(option ? [option] : undefined);
  };

  //Stories API call to get details of options from SB
  useEffect(() => {
    if (sbData && sbData?.length === 0) {
      const url = `${sbEndpoint}${SBENDPOINT.STORIES_URL}`;
      let groupList = '';
      const standaloneUids: string[] = [];
      (content?.content || [])?.forEach((opt: any) => {
        if (
          opt?.component === 'optionsCard' &&
          opt?.isStandAloneOptions &&
          opt?.standaloneOptions?.length
        ) {
          standaloneUids.push(...opt.standaloneOptions);
        } else if (opt?.component === 'optionsCard' && opt?.optionsGroup !== '') {
          groupList = groupList?.concat(`${opt?.optionsGroup?.toLowerCase()},`);
        }
      });
      let filterQuery = `filter_query[isSellable][is]=true&filter_query[tvStreamingSubCategoryList][in]=${groupList}&filter_query[component][in]=optionProductBasePage&per_page=50&language=${activelocaleId}`;
      if (standaloneUids.length > 0) {
        // Query by uids
        filterQuery = `filter_query[isSellable][is]=true&filter_query[component][in]=optionProductBasePage&per_page=50&language=${activelocaleId}`;
      }

      const params: any = {
        type: 'B2C',
        starts_with: mainContent?.optionCatalogPath ?? '',
        filterQueryParam: filterQuery
      };

      if (standaloneUids.length > 0) {
        params.by_uuids = standaloneUids.join(',');
      }

      CMSStoryblokServiceV2.get(url, params)
        .then((data) => {
          if (data?.data?.stories?.length > 0) {
            setSbData(data?.data?.stories);
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.log(err);
        });
    }

    trackNewDatalayerEvent(VIEWPROMOTION, VIEWPROMOTION, {
      banner_promotion_name: content?.title,
      banner_source: CMS,
      banner_type: VIEWOPTIONPOPUP,
      banner_location: content?.bannerLocation,
      audience_name: ALL,
      promotion_objective: ALL,
      index: 1
    });
  }, [content]);

  // Helper to map option data with SB data with copy info
  const mapOptionData = (
    opt: any,
    sbData: any[],
    beneficiaryProduct: any[],
    selectedIdslist: any[],
    includedOptions: any[]
  ) => {
    const optionData = sbData?.find((i: any) => i?.content?.code === opt?.id);
    let defaultBeneficiaryPrice = 0;
    let benefitDuration = 0;
    if (beneficiaryProduct?.length > 0) {
      const beneficiaryPrice = beneficiaryProduct?.find((prod: any) => prod?.id === opt?.id);

      if (beneficiaryPrice) {
        defaultBeneficiaryPrice = beneficiaryPrice?.derivedPrice;
        benefitDuration = beneficiaryPrice?.benefitDuration;
      }
    }

    const data = {
      ...opt,
      uuid: optionData?.uuid,
      count: selectedIdslist?.includes(opt?.id) ? 1 : 0,
      disable: includedOptions?.includes(opt?.id) ? 1 : 0,
      beneficiaryPrice: defaultBeneficiaryPrice,

      ...(optionData?.content?.displayName && { title: optionData?.content?.displayName }),
      ...(optionData?.content?.features && { features: optionData?.content?.features }),
      ...(optionData?.content?.badge && { badge: optionData?.content?.badge }),
      ...(optionData?.content?.prodImage?.filename && {
        prodImage: optionData?.content?.prodImage?.filename
      })
    };

    return data?.title ? data : null;
  };

  // To get the details of the options from SB based on the option
  useEffect(() => {
    if (updateResponseList && optionsResponse?.length > 0) {
      updateResponseList(optionsResponse);
    }
    if (sbData?.length > 0 && optionsResponse?.length > 0 && content?.content?.length > 0) {
      const selectedIdslist: any[] = [];
      const beneficiaryProduct: any[] = [];
      const includedOptions: any[] = [];

      (preSelectedOptions || []).forEach((sel: any) => {
        if (sel?.beneficiaryProduct?.length > 0) {
          beneficiaryProduct.push(...sel?.beneficiaryProduct);
        }
        if (sel?.includedOption?.length > 0) {
          includedOptions.push(...sel?.includedOption);
        }
        selectedIdslist.push(sel?.id);
      });

      const groupedOptions: any = {};

      (content?.content || []).forEach((comp: any) => {
        if (comp?.component !== 'optionsCard') return;

        let optsList: any[] = [];
        let groupWithOptions: any;

        if (comp?.isStandAloneOptions) {
          // comp?.standaloneOptions is true, so groupWithOptions is an array
          groupWithOptions = [...optionsResponse];
          groupWithOptions.forEach((group: any) => {
            if (group?.serviceOption?.length > 0) {
              const mapped = group.serviceOption
                .map((opt: any) =>
                  mapOptionData(opt, sbData, beneficiaryProduct, selectedIdslist, includedOptions)
                )
                .filter(Boolean);
              optsList = optsList.concat(mapped);
            }
          });
        } else {
          groupWithOptions = (optionsResponse || []).find(
            (resp: any) => resp?.group?.toLowerCase() === comp?.optionsGroup?.toLowerCase()
          );
          if (groupWithOptions?.serviceOption?.length > 0) {
            optsList = groupWithOptions.serviceOption
              .map((opt: any) =>
                mapOptionData(opt, sbData, beneficiaryProduct, selectedIdslist, includedOptions)
              )
              .filter(Boolean);
          }
        }

        if (optsList.length > 0) {
          if (comp?.isStandAloneOptions && comp?.standaloneOptions?.length > 0) {
            // Order optsList by the order of uuids in comp.standaloneOptions
            optsList.sort((a, b) => {
              const idxA = comp.standaloneOptions.indexOf(a.uuid);
              const idxB = comp.standaloneOptions.indexOf(b.uuid);
              return idxA - idxB;
            });
          }
          groupedOptions[`${comp?.optionsGroup}`] = optsList;
        }
      });

      if (groupedOptions && Object.keys(groupedOptions).length > 0) {
        setOptionsList(groupedOptions);
      }
    }
  }, [sbData, optionsResponse]);
  const skipToCheckoutPosition = content?.skipToCheckoutPosition || 'responsive';
  const hasBenefitDurationOption = Object.values(optionsList || {}).some((optionGroup: any) =>
    (optionGroup || []).some((opt: any) => opt?.benefitDuration > 0)
  );

  return (
    <div className={`ott-options-small-pop-up ${styles.popUpWrapper}`}>
      <div className={styles.headerSection}>
        <h3>{content?.title}</h3>
        <p>{content?.subtitle}</p>
        {showPrice ? (
          <>
            <div className={styles.priceSectionTitle}>{content?.priceSectionTitle}</div>
            <div className={styles.priceSection}>
              <h3>{productData?.productName}</h3>
              <DynamicPriceSection
                priceCollection={showPrice}
                constants={constants}
                promoText={productData?.promoText}
                deviceFlow={deviceFlow}
                content={content}
                offeringData={offeringData}
                uniqueId={uniqueId}
              />
            </div>
          </>
        ) : null}
      </div>
      {(viewPort === 'mobile' && skipToCheckoutPosition === 'responsive') ||
      content?.skipToCheckoutPosition === 'top' ? (
        <div className={styles.skipWrapper}>
          {content?.secondaryBtn?.length > 0 && (
            <FalconButtonV2 content={content?.skipToCheckout?.[0]} onClick={() => clickHandler()} />
          )}
          <p>{content?.footerNote}</p>
        </div>
      ) : null}

      {(content?.content || [])?.map((comp: any, index: number) => (
        <div className={styles.mainWrapper} key={index}>
          {comp?.component === 'richText' && comp?.text && <RichText content={comp?.text} />}{' '}
          {comp?.component === 'optionsCard' && optionsList?.[`${comp?.optionsGroup}`] && (
            <CarouselComponent
              carouselIndex={0}
              arrow={mainContent?.enableMobileArrows}
              swipeScrollTolerance={5}
              swipeable={true}
              responsive={{
                superLargeDesktop: {
                  breakpoint: { max: 10000, min: 1440 },
                  items: 3
                }
              }}
            >
              {optionsList &&
                Object?.keys(optionsList)?.length > 0 &&
                optionsList?.[`${comp?.optionsGroup}`] &&
                (optionsList?.[`${comp?.optionsGroup}`] || [])?.map(
                  (optionsData: any, index: number) =>
                    optionsData?.title ? (
                      <div
                        className={`${styles.carouselItem} ${
                          optionsData?.id === selectedOption?.id && styles.selectedOption
                        } ${optionsData?.disable > 0 && styles.disableCard}`}
                        key={index}
                      >
                        {optionsData?.badge && (
                          <div className={styles.badge}>{optionsData?.badge}</div>
                        )}
                        {optionsData?.id === selectedOption?.id && (
                          <div className={styles.overlayIcon}>
                            <img
                              src={`${assetPath}/icons/lineCheck-success.svg`}
                              width={26}
                              height={26}
                              alt='success'
                            />
                          </div>
                        )}
                        <div>
                          <div className={styles.titleWrapper}>
                            {optionsData?.prodImage ? (
                              <img
                                src={optionsData?.prodImage}
                                alt=''
                                width={32}
                                height={32}
                                className={styles.planIcon}
                              />
                            ) : null}

                            <h3 className='fw-bold'>{optionsData?.title}</h3>
                          </div>
                          {optionsData?.features && (
                            <div className={styles.features}>
                              <RichText content={optionsData?.features} />
                            </div>
                          )}
                        </div>

                        <div>
                          <div className={styles.horizontalLine}></div>
                          <div className={styles.priceSection}>
                            <div className={styles.actualPrice}>
                              {optionsData?.derivedPrice
                                ? `+${countZero(optionsData?.derivedPrice)}`
                                : `+${countZero(optionsData?.recurringPrice)}`}
                              <span>{constants?.month}</span>
                            </div>
                            <div className={styles.basePrice}>
                              {(optionsData?.derivedPrice || optionsData?.beneficiaryPrice > 0) &&
                                optionsData?.recurringPrice > 0 && (
                                  <>
                                    {`+${countZero(optionsData?.recurringPrice)}`}
                                    <span>{constants?.month}</span>
                                  </>
                                )}
                            </div>
                          </div>
                          {hasBenefitDurationOption && (
                            <div className={styles.benefitDurationText}>
                              {optionsData.benefitDuration > 0 && content?.benefitDurationText
                                ? content?.benefitDurationText
                                    ?.replace('{0}', countZero(optionsData?.recurringPrice))
                                    ?.replace('{1}', optionsData.benefitDuration + 1)
                                : null}
                            </div>
                          )}

                          <div>
                            <button
                              className={`primarybutton`}
                              onClick={() => clickHandler(optionsData, index)}
                            >
                              <span className={styles.buttontext}>
                                {optionsData?.id === selectedOption?.id
                                  ? content?.removeSelection
                                  : content?.proceedToCheckout}
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : null
                )}
            </CarouselComponent>
          )}
        </div>
      ))}
      {Object?.keys(optionsList || [])?.length === 0 && <OttServiceOptionsLoader />}
      {(viewPort !== 'mobile' && skipToCheckoutPosition === 'responsive') ||
      content?.skipToCheckoutPosition === 'bottom' ? (
        <div className={styles.skipWrapper}>
          {content?.secondaryBtn?.length > 0 && (
            <FalconButtonV2
              content={content?.skipToCheckout?.[0]}
              onClick={() => {
                trackNewDatalayerEvent(GAEVENT, CTABUTTONCLICK, {
                  click_text: content?.skipToCheckout?.[0]?.buttonLabel || 'skip_to_checkout'
                });
                clickHandler();
              }}
            />
          )}
          <p>{content?.footerNote}</p>
        </div>
      ) : null}
    </div>
  );
};

export default OttServiceOptionsSmallPopup;
